<template>
  <div>
    <!-- «Сообщить об ошибке»: bottom right of every page after sign-in -->
    <v-btn
      class="feedback-fab"
      icon
      color="primary"
      size="large"
      elevation="6"
      aria-label="Сообщить об ошибке"
      @click="open"
    >
      <v-icon>mdi-message-alert-outline</v-icon>
      <v-tooltip activator="parent" location="left">Сообщить об ошибке</v-tooltip>
    </v-btn>

    <!-- closes on ESC and a click outside; the text stays in the form until it is sent -->
    <v-dialog v-model="visible" max-width="640px">
      <!-- a picture pasted from the clipboard (Ctrl/Cmd+V) is attached like a chosen file -->
      <v-card @paste="onPaste">
        <v-card-title>
          <span class="text-h5">Сообщить об ошибке</span>
        </v-card-title>
        <v-card-text>
          <v-form ref="form" @submit.prevent="send">
            <v-btn-toggle v-model="form.kind" mandatory color="primary" variant="outlined" density="comfortable" class="mb-4">
              <v-btn value="bug">Ошибка</v-btn>
              <v-btn value="improvement">Улучшение</v-btn>
            </v-btn-toggle>
            <v-textarea
              v-model="form.steps"
              label="Последовательность действий"
              placeholder="Что вы делали, по шагам"
              variant="outlined"
              rows="3"
              auto-grow
              counter="5000"
              :rules="rules"
            ></v-textarea>
            <v-textarea
              v-model="form.expected"
              label="Что ожидали получить"
              variant="outlined"
              rows="2"
              auto-grow
              counter="5000"
              :rules="rules"
            ></v-textarea>
            <v-textarea
              v-model="form.actual"
              label="Что получили"
              variant="outlined"
              rows="2"
              auto-grow
              counter="5000"
              :rules="rules"
            ></v-textarea>
            <!-- screenshots: up to 3 JPG, PNG or GIF of up to 3 MB; core checks them again -->
            <div class="mb-3">
              <input ref="fileInput" type="file" accept="image/jpeg,image/png,image/gif" multiple hidden @change="onFiles">
              <v-btn variant="tonal" size="small" prepend-icon="mdi-paperclip"
                     :disabled="shots.length >= MAX_SHOTS" @click="$refs.fileInput.click()">
                Приложить скриншот
              </v-btn>
              <span class="text-caption text-medium-emphasis ml-2">
                до {{ MAX_SHOTS }} файлов JPG, PNG или GIF, до 3 МБ; можно вставить из буфера (Ctrl/Cmd+V)
              </span>
              <div v-if="shots.length" class="d-flex flex-wrap ga-2 mt-2">
                <div v-for="(shot, i) in shots" :key="shot.url" class="shot">
                  <img :src="shot.url" :alt="shot.name">
                  <v-btn icon="mdi-close" size="x-small" density="comfortable" class="shot-remove"
                         :aria-label="`Убрать ${shot.name}`" @click="removeShot(i)"></v-btn>
                </div>
              </div>
              <div v-if="shotError" class="text-caption text-error mt-1">{{ shotError }}</div>
            </div>
            <div class="text-caption text-medium-emphasis">
              К сообщению приложатся адрес страницы, данные об устройстве и браузере, IP-адрес и журнал страницы
              (<router-link :to="privacyLink" target="_blank">политика обработки данных</router-link>).
            </div>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn variant="text" :disabled="sending" @click="visible = false">Отменить</v-btn>
          <v-btn color="primary" variant="elevated" :loading="sending" @click="send">Отправить</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import apiClient from '@/apiClient'
import { getEntries } from '@/utils/clientLog'
import { collectClientInfo } from '@/utils/clientInfo'
import { LEGAL_LINKS } from '@/config/legal'

const EMPTY = { kind: 'bug', steps: '', expected: '', actual: '' }
const MAX_SHOTS = 3
const MAX_SHOT_BYTES = 3 * 1024 * 1024
const SHOT_TYPES = ['image/jpeg', 'image/png', 'image/gif']

// the file as base64 without the "data:...;base64," prefix
function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result).split(',', 2)[1] || '')
    reader.onerror = () => reject(reader.error)
    reader.readAsDataURL(file)
  })
}

export default {
  name: 'FeedbackButton',
  emits: ['show-message'],
  data() {
    return {
      visible: false,
      sending: false,
      privacyLink: LEGAL_LINKS.privacy,
      // the page where the button was pressed, taken before the dialog opened
      page: { url: '', title: '' },
      form: { ...EMPTY },
      MAX_SHOTS,
      shots: [],       // { file, name, url } of the pictures to attach
      shotError: '',
      rules: [
        v => !!(v && v.trim()) || 'Заполните поле',
        v => (v || '').length <= 5000 || 'Не больше 5000 символов'
      ]
    }
  },
  methods: {
    open() {
      this.page = { url: window.location.href, title: document.title }
      this.visible = true
    },
    addFiles(files) {
      this.shotError = ''
      for (const file of files) {
        if (this.shots.length >= MAX_SHOTS) {
          this.shotError = `Можно приложить не больше ${MAX_SHOTS} файлов`
          break
        }
        const name = file.name || `screenshot-${this.shots.length + 1}.png`
        if (!SHOT_TYPES.includes(file.type)) {
          this.shotError = `«${name}» не картинка JPG, PNG или GIF`
          continue
        }
        if (file.size > MAX_SHOT_BYTES) {
          this.shotError = `«${name}» больше 3 МБ`
          continue
        }
        this.shots.push({ file, name, url: URL.createObjectURL(file) })
      }
    },
    onFiles(event) {
      this.addFiles(Array.from(event.target.files || []))
      event.target.value = ''  // the same file may be chosen again after removing it
    },
    onPaste(event) {
      const files = Array.from(event.clipboardData?.items || [])
        .filter(item => item.kind === 'file' && item.type.startsWith('image/'))
        .map(item => item.getAsFile())
        .filter(Boolean)
      if (files.length) {
        event.preventDefault()
        this.addFiles(files)
      }
    },
    removeShot(i) {
      URL.revokeObjectURL(this.shots[i].url)
      this.shots.splice(i, 1)
      this.shotError = ''
    },
    clearShots() {
      this.shots.forEach(shot => URL.revokeObjectURL(shot.url))
      this.shots = []
      this.shotError = ''
    },
    async send() {
      const { valid } = await this.$refs.form.validate()
      if (!valid) return
      this.sending = true
      try {
        await apiClient.post('/feedback', {
          ...this.form,
          page_url: this.page.url,
          page_title: this.page.title,
          client: await collectClientInfo(),
          logs: getEntries(),
          screenshots: await Promise.all(this.shots.map(async shot => ({ name: shot.name, data: await toBase64(shot.file) })))
        })
        this.visible = false
        this.form = { ...EMPTY }
        this.clearShots()
        this.$emit('show-message', 'Спасибо за ваше сообщение!', false)
      } catch (e) {
        // the text stays in the form
        this.$emit('show-message', e.response?.data?.detail && typeof e.response.data.detail === 'string'
          ? e.response.data.detail : 'Не удалось отправить сообщение, попробуйте позже', true)
      } finally {
        this.sending = false
      }
    }
  }
}
</script>

<style scoped>
.shot {
  position: relative;
  width: 96px;
  height: 72px;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 4px;
  overflow: hidden;
}
.shot img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.shot-remove {
  position: absolute;
  top: 2px;
  right: 2px;
}
.feedback-fab {
  position: fixed;
  right: 24px;
  bottom: 24px;
  z-index: 1005;
}
</style>
